import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Alert, CircularProgress, TextField, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, Card, CardContent,
  useMediaQuery, useTheme,
} from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import axios from 'axios';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TERM_LABEL = { 1: 'Term 1', 2: 'Term 2', 3: 'Term 3' };

// Read-only class timetable with a class filter. The class teacher lands on their
// own class and can filter to any other class to see its published grid. It never
// mutates — viewing only. `initialClassCode` seeds the selection from the centre.
export default function ClassTimetableView({ initialClassCode = '' }) {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [classCode, setClassCode] = useState(initialClassCode);
  const [pathway, setPathway] = useState('');   // '' = whole class (option blocks shown as options)
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const load = useCallback(async (cc, pw) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (cc) params.classCode = cc;
      if (pw) params.pathway = pw;
      const { data } = await axios.get(`${API}/api/timetable/class-view`, { headers: hdrs(), params });
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the timetable.');
    } finally { setLoading(false); }
  }, []);

  // Reload whenever the chosen class or pathway changes (and on mount).
  useEffect(() => { load(classCode, pathway); }, [classCode, pathway, load]);

  // Follow the centre's class selection until the user picks a class here.
  useEffect(() => { if (initialClassCode) setClassCode(initialClassCode); }, [initialClassCode]);

  // Reset the pathway view when switching classes (options differ per class).
  useEffect(() => { setPathway(''); }, [classCode]);

  const availablePathways = data?.availablePathways || [];

  const lessonPeriods = useMemo(() => {
    const periods = data?.config?.periods || [];
    return periods.filter(p => p.type === 'lesson').sort((a, b) => a.periodNo - b.periodNo);
  }, [data]);

  const days = data?.config?.daysOfWeek || [];

  const cellMap = useMemo(() => {
    const m = {};
    (data?.entries || []).forEach(e => { m[`${e.day}-${e.periodNo}`] = e; });
    return m;
  }, [data]);

  const periodLabel = (p) => p.lessonNo ? `L${p.lessonNo}` : `P${p.periodNo}`;

  const classes = data?.classes || [];

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
        <CalendarMonth sx={{ color: '#00838f' }} />
        <Typography variant="h6" fontWeight={700}>Class Timetable</Typography>
        {data?.published && (
          <Chip size="small" variant="outlined"
            label={`${data.academicYear} · ${TERM_LABEL[data.term] || `Term ${data.term}`}`} sx={{ ml: 0.5 }} />
        )}
        <Chip size="small" label="Read-only" variant="outlined" color="default" sx={{ ml: 'auto' }} />
      </Box>

      <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
        <TextField
          select
          label="View class"
          value={classCode}
          onChange={e => setClassCode(e.target.value)}
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 260 } }}
        >
          <MenuItem value="" disabled>Select a class…</MenuItem>
          {classes.map(c => (
            <MenuItem key={c.classCode} value={c.classCode}>{c.className}</MenuItem>
          ))}
        </TextField>

        {/* Pathway selector — only when this class actually splits by pathway
            (e.g. G10 with EMAT/CMAT). Picking one shows that pathway's timetable. */}
        {classCode && availablePathways.length > 0 && (
          <TextField
            select
            label="Pathway view"
            value={pathway}
            onChange={e => setPathway(e.target.value)}
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 220 } }}
            helperText="Splits option blocks by pathway"
          >
            <MenuItem value="">Whole class (all options)</MenuItem>
            {availablePathways.map(p => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </TextField>
        )}

        {pathway && (
          <Chip size="small" color="primary" variant="outlined"
            label={`${pathway} timetable`} sx={{ alignSelf: 'center' }} />
        )}
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {data && !data.published && (
            <Alert severity="info">No published timetable yet. It will appear here once the school publishes it.</Alert>
          )}

          {data?.published && !classCode && (
            <Alert severity="info">Pick a class above to view its timetable.</Alert>
          )}

          {data?.published && classCode && (data.entries || []).length === 0 && (
            <Alert severity="info">No lessons placed on this class's timetable yet.</Alert>
          )}

          {data?.published && classCode && (data.entries || []).length > 0 && (
            isMobile ? (
              /* ── Phone: grouped by day, lessons only ── */
              <Box display="flex" flexDirection="column" gap={1.5}>
                {days.map(day => {
                  const dayLessons = lessonPeriods
                    .map(p => ({ p, e: cellMap[`${day}-${p.periodNo}`] }))
                    .filter(x => x.e);
                  return (
                    <Card key={day} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography fontWeight={700} color="primary" gutterBottom>{day}</Typography>
                        {dayLessons.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">No lessons</Typography>
                        ) : dayLessons.map(({ p, e }) => (
                          <Box key={p.periodNo} display="flex" gap={1.2} py={0.6}
                            sx={{ borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
                            <Box sx={{ minWidth: 76 }}>
                              <Typography variant="caption" fontWeight={700}>{periodLabel(p)}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">{p.startTime}–{p.endTime}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{e.subject || '—'}</Typography>
                              {e.teacher && <Typography variant="caption" color="text.secondary">{e.teacher}</Typography>}
                            </Box>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              /* ── Desktop: weekly grid ── */
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 640, '& td, & th': { border: '1px solid', borderColor: 'divider' } }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ width: 96 }}><strong>Period</strong></TableCell>
                      {days.map(d => <TableCell key={d} align="center"><strong>{d}</strong></TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lessonPeriods.map(p => (
                      <TableRow key={p.periodNo}>
                        <TableCell sx={{ bgcolor: 'grey.50' }}>
                          <Typography variant="caption" fontWeight={700} display="block">{periodLabel(p)}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.startTime}–{p.endTime}</Typography>
                        </TableCell>
                        {days.map(day => {
                          const e = cellMap[`${day}-${p.periodNo}`];
                          return (
                            <TableCell key={day} align="center" sx={{ verticalAlign: 'top', bgcolor: e ? '#00838f0d' : 'inherit' }}>
                              {e ? (
                                <>
                                  <Typography variant="body2" fontWeight={600} lineHeight={1.2}>{e.subject || '—'}</Typography>
                                  {e.teacher && <Typography variant="caption" color="text.secondary">{e.teacher}</Typography>}
                                </>
                              ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )
          )}
        </>
      )}
    </Box>
  );
}
